import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router";
import { InlineWidget } from "react-calendly";
import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  ChevronDown,
  Clock,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Send,
  ShieldCheck,
  Sparkles,
  Users,
  Zap,
} from "lucide-react";
import Footer from "../../components/footer/Footer";
import { submitContactForm } from "../../api/consumerContactApi";
import showToast from "../../utils/toast";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.55, ease: "easeOut", delay },
});

const interests = [
  "Sponsor a subcategory",
  "Publish provider content",
  "Route leads to my team",
  "SMS campaign partnership",
  "Ask a question",
  "Book an appointment",
  "Consumer support",
  "Other",
];

const contactMethods = [
  {
    icon: Phone,
    label: "Call us",
    value: "+233 20 000 0000",
    sub: "Mon–Fri, 8AM–8PM",
    href: "tel:+233200000000",
    bg: "bg-[#FFD221]",
    text: "text-black",
  },
  {
    icon: Mail,
    label: "Email us",
    value: "info@infoboxx.com",
    sub: "We reply within 24h",
    href: "mailto:info@infoboxx.com",
    bg: "bg-gray-950",
    text: "text-white",
  },
  {
    icon: MessageCircle,
    label: "WhatsApp",
    value: "Chat directly",
    sub: "Quick, conversational support",
    href: "#",
    bg: "bg-white",
    text: "text-black",
  },
  {
    icon: MapPin,
    label: "Visit us",
    value: "Abelemkpe, Accra",
    sub: "1st Floor GWOSEVO Building",
    href: "#",
    bg: "bg-[#f7f4ec]",
    text: "text-black",
  },
];

const nextSteps = [
  {
    icon: Zap,
    title: "We classify your request",
    text: "Your message is categorised by type and intent — partner inquiry, user question, or support.",
  },
  {
    icon: Users,
    title: "A team member reaches out",
    text: "The right person contacts you within 24–48 hours to understand fit and scope.",
  },
  {
    icon: CheckCircle2,
    title: "We map your journey",
    text: "For partners: we outline your category lane, content structure, and lead path.",
  },
];

const quickTopics = [
  {
    label: "Become a Partner",
    href: "/partners#how-to-become-a-partner",
    icon: "🤝",
  },
  { label: "Explore Categories", href: "/#categories", icon: "🗂️" },
  {
    label: "Book an Appointment",
    href: "/contact#contact-book-appointment",
    icon: "📅",
  },
];

/** Same Calendly destination as `BookAppointment.jsx` */
const CALENDLY_URL = "https://calendly.com/achiekobby17/test";

const ContactPage = () => {
  const location = useLocation();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [interest, setInterest] = useState("");
  const [otherInterest, setOtherInterest] = useState("");
  const [contactMode, setContactMode] = useState("message");
  const [showCalendly, setShowCalendly] = useState(false);
  const [form, setForm] = useState({
    fullName: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });
  const calendlyRef = useRef(null);
  const bookAppointmentRef = useRef(null);
  const getInTouchRef = useRef(null);

  const clearFormState = () => {
    setSubmitted(false);
    setSubmitting(false);
    setInterest("");
    setOtherInterest("");
    setContactMode("message");
    setShowCalendly(false);
    setForm({ fullName: "", company: "", email: "", phone: "", message: "" });
  };

  const openBooking = () => {
    setContactMode("book");
    setShowCalendly(true);
    requestAnimationFrame(() => {
      bookAppointmentRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const applyAppointmentHash = useCallback(() => {
    const hash = location.hash;
    if (hash !== "#contact-calendly" && hash !== "#contact-book-appointment") return;

    setContactMode("book");
    if (hash === "#contact-calendly") setShowCalendly(true);

    window.setTimeout(() => {
      getInTouchRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      window.setTimeout(() => {
        bookAppointmentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 280);
    }, 180);
  }, [location.hash]);

  useEffect(() => {
    applyAppointmentHash();
  }, [location.pathname, location.hash, applyAppointmentHash]);

  /** Prefill message from home "Get Answers" (`/contact?message=…`) or router state. */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromQuery = params.get("message");
    const fromState = location.state?.prefilledMessage;
    const raw = (fromQuery || fromState || "").trim();
    if (!raw) return undefined;

    let decoded = raw;
    try {
      decoded = decodeURIComponent(raw);
    } catch {
      decoded = raw;
    }

    setContactMode("message");
    setShowCalendly(false);
    setSubmitted(false);
    setInterest("Ask a question");
    setForm((prev) => ({ ...prev, message: decoded }));

    const scrollTimer = window.setTimeout(() => {
      getInTouchRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);

    return () => window.clearTimeout(scrollTimer);
  }, [location.pathname, location.search, location.state]);

  const selectInterest = (item) => {
    setInterest(item);
    if (item !== "Other") setOtherInterest("");
    if (item === "Book an appointment") {
      setContactMode("book");
      window.setTimeout(() => {
        getInTouchRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        bookAppointmentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  };

  const handleMessageSubmit = async (e) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const otherEl = document.getElementById("interest-other-specify");
    if (interest === "Other" && !otherInterest.trim()) {
      if (otherEl) {
        otherEl.setCustomValidity("Please describe what you need.");
        otherEl.reportValidity();
        otherEl.setCustomValidity("");
      }
      return;
    }
    if (!interest.trim()) {
      showToast.error("Please select what you are interested in.");
      return;
    }
    if (!formEl.checkValidity()) {
      formEl.reportValidity();
      return;
    }

    setSubmitting(true);
    const result = await submitContactForm({
      fullName: form.fullName,
      company: form.company,
      email: form.email,
      phone: form.phone,
      interest,
      interestOther: otherInterest,
      message: form.message,
      contactMode: "message",
    });
    setSubmitting(false);

    if (!result.status) {
      showToast.error(result.message);
      return;
    }

    showToast.success(result.message);
    setSubmitted(true);
  };

  const updateForm = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  return (
    <div className="min-h-screen bg-[#f7f4ec] [font-family:'Onest',sans-serif]">
      {/* ── HERO ───────────────────────────────── */}
      <section className="relative overflow-hidden bg-white px-6 pb-20 pt-32 lg:px-12">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-[#FFD221]/20 blur-[120px]" />
          <div className="absolute right-[-10rem] top-12 h-[30rem] w-[30rem] rounded-full bg-[#f7f4ec] blur-[90px]" />
          <div
            className="absolute inset-0 opacity-[0.45]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px,rgba(0,0,0,.1) 1px,transparent 0)",
              backgroundSize: "32px 32px",
            }}
          />
        </div>

        <div className="relative mx-auto max-w-7xl">
          <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
            {/* left copy */}
            <div>
              <motion.span
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-black shadow-sm"
              >
                <Sparkles size={13} />
                Contact InfoBoxx
              </motion.span>

              <motion.h1
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08, duration: 0.6 }}
                className="mt-7 max-w-3xl text-5xl font-black leading-[1.02] tracking-tight text-black sm:text-6xl lg:text-[4.5rem]"
              >
                Let&apos;s start a{" "}
                <span className="relative inline-block">
                  <span className="bg-gradient-to-r from-[#b8860b] via-[#FFD221] to-[#c9a000] bg-clip-text text-transparent">
                    conversation
                  </span>
                  <svg
                    className="absolute -bottom-2 left-0 h-3 w-full"
                    viewBox="0 0 200 12"
                    fill="none"
                    preserveAspectRatio="none"
                    aria-hidden
                  >
                    <path
                      d="M3 8C52 2 145 2 197 8"
                      stroke="#FFD221"
                      strokeWidth="4"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                .
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.16, duration: 0.55 }}
                className="mt-7 max-w-xl text-lg leading-relaxed text-gray-600"
              >
                Whether you want to partner with us, ask a question, book a
                consultation, or explore what InfoBoxx can do — we are here and
                ready.
              </motion.p>

              {/* quick topic chips */}
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.24, duration: 0.5 }}
                className="mt-8 flex flex-wrap gap-3"
              >
                {quickTopics.map(({ label, href, icon }) => (
                  <Link
                    key={label}
                    to={href}
                    className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-[#f7f4ec] px-4 py-2 text-xs font-black text-black transition hover:border-[#FFD221] hover:bg-[#FFD221]"
                  >
                    <span>{icon}</span>
                    {label}
                  </Link>
                ))}
              </motion.div>
            </div>

            {/* right: contact method cards */}
            <motion.div
              initial={{ opacity: 0, x: 28 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15, duration: 0.65 }}
              className="grid grid-cols-2 gap-4"
            >
              {contactMethods.map(
                ({ icon: Icon, label, value, sub, href, bg, text }, index) => (
                  <motion.a
                    key={label}
                    href={href}
                    whileHover={{ y: -4 }}
                    className={`group flex flex-col justify-between rounded-3xl border border-black/[0.07] p-6 shadow-sm transition hover:shadow-xl ${bg} ${text}`}
                    style={{ minHeight: 170 }}
                  >
                    <div
                      className={`flex h-11 w-11 items-center justify-center rounded-2xl ${
                        bg === "bg-[#FFD221]"
                          ? "bg-black/10"
                          : bg === "bg-gray-950"
                            ? "bg-[#FFD221]"
                            : "bg-[#FFD221]/20"
                      }`}
                    >
                      <Icon
                        size={20}
                        className={bg === "bg-gray-950" ? "text-black" : ""}
                      />
                    </div>
                    <div>
                      <p
                        className={`text-[10px] font-black uppercase tracking-[0.2em] ${
                          bg === "bg-gray-950"
                            ? "text-gray-500"
                            : "text-gray-500"
                        } ${bg === "bg-[#FFD221]" ? "text-black/50" : ""}`}
                      >
                        {label}
                      </p>
                      <p className="mt-1 text-base font-black">{value}</p>
                      <p
                        className={`mt-0.5 text-xs ${
                          bg === "bg-gray-950"
                            ? "text-gray-400"
                            : "text-gray-500"
                        } ${bg === "bg-[#FFD221]" ? "text-black/60" : ""}`}
                      >
                        {sub}
                      </p>
                    </div>
                  </motion.a>
                ),
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── FORM + SIDEBAR ─────────────────────── */}
      <section ref={getInTouchRef} id="contact-get-in-touch" className="scroll-mt-28 px-6 py-20 lg:px-12">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1fr_380px] lg:items-start">
          {/* form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="overflow-hidden rounded-[2.5rem] border border-black/[0.07] bg-white shadow-[0_32px_80px_-44px_rgba(0,0,0,0.45)]"
          >
            <div className="h-1.5 w-full bg-gradient-to-r from-[#FFD221] via-[#c9a000] to-[#ffec80]" />
            <div className="p-8 sm:p-10">
              <AnimatePresence mode="wait">
                {submitted ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex min-h-[440px] flex-col items-center justify-center text-center"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 200,
                        delay: 0.1,
                      }}
                      className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[#FFD221] shadow-[0_20px_44px_-20px_rgba(255,210,33,0.6)]"
                    >
                      <CheckCircle2 size={36} className="text-black" />
                    </motion.div>
                    <h2 className="text-3xl font-black text-black">
                      Message received!
                    </h2>
                    <p className="mt-3 max-w-sm text-sm leading-relaxed text-gray-500">
                      Your request has been classified and queued. A team member
                      will reach out within 24–48 hours.
                    </p>
                    <div className="mt-6 flex gap-3">
                      <button
                        type="button"
                        onClick={clearFormState}
                        className="rounded-xl border border-black/10 bg-[#f7f4ec] px-6 py-3 text-sm font-black text-black transition hover:border-black/20"
                      >
                        Send another
                      </button>
                      <Link
                        to="/"
                        className="inline-flex items-center gap-2 rounded-xl bg-[#FFD221] px-6 py-3 text-sm font-black text-black transition hover:bg-[#e6bc00]"
                      >
                        Back to home
                        <ArrowRight size={15} />
                      </Link>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="contact-main"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <div className="mb-8 flex flex-col gap-5 border-b border-black/[0.08] pb-8 lg:flex-row lg:items-start lg:justify-between">
                      <div className="max-w-xl">
                        <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#c9a000]">
                          Get in touch
                        </p>
                        <h2 className="mt-2 text-3xl font-black text-black sm:text-4xl">
                          {contactMode === "message"
                            ? "Tell us what you need."
                            : "Book a consultation."}
                        </h2>
                        <p className="mt-3 text-sm leading-relaxed text-gray-500">
                          {contactMode === "message"
                            ? "Send a message — we classify by intent and respond within one business day."
                            : "Use the same live Calendly flow as on our homepage, or switch back to the form anytime."}
                        </p>
                      </div>
                      <div
                        className="flex w-full shrink-0 rounded-2xl border border-black/10 bg-[#f7f4ec] p-1.5 shadow-inner sm:w-auto"
                        role="tablist"
                        aria-label="Choose contact method"
                      >
                        <button
                          type="button"
                          role="tab"
                          aria-selected={contactMode === "message"}
                          onClick={() => {
                            setContactMode("message");
                            setShowCalendly(false);
                          }}
                          className={`flex-1 rounded-xl px-3 py-3 text-center text-xs font-black transition sm:flex-none sm:px-5 sm:text-sm ${
                            contactMode === "message"
                              ? "bg-white text-black shadow-sm"
                              : "text-gray-500 hover:text-black"
                          }`}
                        >
                          Send a message
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={contactMode === "book"}
                          onClick={() => setContactMode("book")}
                          className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-3 text-center text-xs font-black transition sm:flex-none sm:px-5 sm:text-sm ${
                            contactMode === "book"
                              ? "bg-[#FFD221] text-black shadow-sm"
                              : "text-gray-500 hover:text-black"
                          }`}
                        >
                          <Calendar size={16} aria-hidden />
                          Book appointment
                        </button>
                      </div>
                    </div>

                    <AnimatePresence mode="wait">
                      {contactMode === "message" ? (
                        <motion.form
                          key="message-form"
                          initial={{ opacity: 0, y: 14 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -12 }}
                          transition={{ duration: 0.28 }}
                          onSubmit={handleMessageSubmit}
                        >
                          <div className="grid gap-4 sm:grid-cols-2">
                            <div className="flex flex-col gap-1.5">
                              <label className="text-[11px] font-black uppercase tracking-widest text-gray-400">
                                Full name *
                              </label>
                              <input
                                className="rounded-2xl border border-black/10 bg-[#f7f4ec] px-4 py-3 text-sm font-medium text-black outline-none transition focus:border-[#FFD221] focus:ring-2 focus:ring-[#FFD221]/25"
                                placeholder="Kwame Mensah"
                                value={form.fullName}
                                onChange={updateForm("fullName")}
                                required
                              />
                            </div>
                            <div className="flex flex-col gap-1.5">
                              <label className="text-[11px] font-black uppercase tracking-widest text-gray-400">
                                Company
                              </label>
                              <input
                                className="rounded-2xl border border-black/10 bg-[#f7f4ec] px-4 py-3 text-sm font-medium text-black outline-none transition focus:border-[#FFD221] focus:ring-2 focus:ring-[#FFD221]/25"
                                placeholder="Your company name"
                                value={form.company}
                                onChange={updateForm("company")}
                              />
                            </div>
                            <div className="flex flex-col gap-1.5">
                              <label className="text-[11px] font-black uppercase tracking-widest text-gray-400">
                                Email *
                              </label>
                              <input
                                type="email"
                                className="rounded-2xl border border-black/10 bg-[#f7f4ec] px-4 py-3 text-sm font-medium text-black outline-none transition focus:border-[#FFD221] focus:ring-2 focus:ring-[#FFD221]/25"
                                placeholder="you@company.com"
                                value={form.email}
                                onChange={updateForm("email")}
                                required
                              />
                            </div>
                            <div className="flex flex-col gap-1.5">
                              <label className="text-[11px] font-black uppercase tracking-widest text-gray-400">
                                Phone / WhatsApp *
                              </label>
                              <input
                                className="rounded-2xl border border-black/10 bg-[#f7f4ec] px-4 py-3 text-sm font-medium text-black outline-none transition focus:border-[#FFD221] focus:ring-2 focus:ring-[#FFD221]/25"
                                placeholder="+233 20 000 0000"
                                value={form.phone}
                                onChange={updateForm("phone")}
                                required
                              />
                            </div>
                          </div>

                          <div className="mt-4 flex flex-col gap-1.5">
                            <label className="text-[11px] font-black uppercase tracking-widest text-gray-400">
                              What are you interested in?
                            </label>
                            {/* 8 options: 4 × 2 grid — equal cells, fixed block height */}
                            <div className="grid min-h-[9.5rem] grid-cols-4 gap-2 sm:min-h-[8.75rem] sm:gap-3">
                              {interests.map((item) => (
                                <button
                                  key={item}
                                  type="button"
                                  onClick={() => selectInterest(item)}
                                  className={`flex min-h-[3.5rem] w-full items-center justify-center rounded-2xl border px-2 py-3 text-center text-[10px] font-black leading-tight transition sm:min-h-[3.75rem] sm:px-3 sm:text-sm sm:leading-snug ${
                                    interest === item
                                      ? "border-[#FFD221] bg-[#FFD221] text-black shadow-[0_10px_30px_-14px_rgba(255,210,33,0.85)]"
                                      : "border-black/10 bg-[#f7f4ec] text-gray-700 hover:border-[#FFD221]/60 hover:bg-white"
                                  }`}
                                >
                                  {item}
                                </button>
                              ))}
                            </div>

                            <AnimatePresence initial={false}>
                              {interest === "Other" && (
                                <motion.div
                                  key="other-specify"
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{
                                    duration: 0.25,
                                    ease: "easeOut",
                                  }}
                                  className="overflow-hidden"
                                >
                                  <div className="pt-4">
                                    <label
                                      htmlFor="interest-other-specify"
                                      className="text-[11px] font-black uppercase tracking-widest text-gray-400"
                                    >
                                      Tell us what you need *
                                    </label>
                                    <input
                                      id="interest-other-specify"
                                      name="interestOther"
                                      type="text"
                                      value={otherInterest}
                                      onChange={(e) =>
                                        setOtherInterest(e.target.value)
                                      }
                                      className="mt-2 w-full rounded-2xl border border-black/10 bg-[#f7f4ec] px-4 py-3 text-sm font-medium text-black outline-none transition placeholder:text-gray-400 focus:border-[#FFD221] focus:ring-2 focus:ring-[#FFD221]/25"
                                      placeholder="e.g. Media partnership, API integration, press inquiry…"
                                      required
                                      autoFocus
                                    />
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>

                          <div className="mt-4 flex flex-col gap-1.5">
                            <label className="text-[11px] font-black uppercase tracking-widest text-gray-400">
                              Your message *
                            </label>
                            <textarea
                              className="h-36 w-full resize-none rounded-2xl border border-black/10 bg-[#f7f4ec] px-4 py-3 text-sm font-medium text-black outline-none transition focus:border-[#FFD221] focus:ring-2 focus:ring-[#FFD221]/25"
                              placeholder="Tell us about your category, question, campaign, or support need..."
                              value={form.message}
                              onChange={updateForm("message")}
                              required
                            />
                          </div>

                          <div className="mt-6 flex flex-wrap items-center gap-4">
                            <button
                              type="submit"
                              disabled={submitting}
                              className="inline-flex items-center gap-2 rounded-2xl bg-black px-8 py-4 text-sm font-black text-white shadow-sm transition hover:bg-[#FFD221] hover:text-black disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {submitting ? "Sending…" : "Send message"}
                              <Send size={16} />
                            </button>
                            <p className="text-xs text-gray-400">
                              We reply within 24 hours.
                            </p>
                          </div>

                          <div className="mt-8 rounded-2xl border border-black/[0.07] bg-[#f7f4ec] p-5 sm:p-6">
                            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#c9a000]">
                              Good to know
                            </p>
                            <div className="mt-4 grid gap-3 sm:grid-cols-3">
                              <div className="rounded-xl border border-black/[0.06] bg-white px-4 py-3">
                                <div className="flex items-center gap-2 text-[#c9a000]">
                                  <Clock size={16} />
                                  <span className="text-xs font-black text-black">
                                    Response time
                                  </span>
                                </div>
                                <p className="mt-1.5 text-[11px] leading-relaxed text-gray-500">
                                  Most messages receive a first reply within one
                                  business day.
                                </p>
                              </div>
                              <div className="rounded-xl border border-black/[0.06] bg-white px-4 py-3">
                                <div className="flex items-center gap-2 text-[#c9a000]">
                                  <ShieldCheck size={16} />
                                  <span className="text-xs font-black text-black">
                                    Your details
                                  </span>
                                </div>
                                <p className="mt-1.5 text-[11px] leading-relaxed text-gray-500">
                                  We use your info only to respond and route
                                  your request — never for spam.
                                </p>
                              </div>
                              <div className="rounded-xl border border-black/[0.06] bg-white px-4 py-3">
                                <div className="flex items-center gap-2 text-[#c9a000]">
                                  <Users size={16} />
                                  <span className="text-xs font-black text-black">
                                    Partners
                                  </span>
                                </div>
                                <p className="mt-1.5 text-[11px] leading-relaxed text-gray-500">
                                  Prefer onboarding? You can go straight to the
                                  partner application.
                                </p>
                                <Link
                                  to="/partners#how-to-become-a-partner"
                                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-black text-black underline-offset-2 hover:underline"
                                >
                                  Partner onboarding
                                  <ArrowRight size={12} />
                                </Link>
                              </div>
                            </div>
                            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-black/[0.08] pt-4 text-[11px] text-gray-500">
                              <span className="font-semibold text-gray-400">
                                Legal
                              </span>
                              <Link
                                to="/privacy"
                                className="font-bold text-gray-600 hover:text-black"
                              >
                                Privacy Policy
                              </Link>
                              <Link
                                to="/terms"
                                className="font-bold text-gray-600 hover:text-black"
                              >
                                Terms & Conditions
                              </Link>
                              <a
                                href="mailto:info@infoboxx.com"
                                className="font-bold text-gray-600 hover:text-black"
                              >
                                info@infoboxx.com
                              </a>
                            </div>
                          </div>
                        </motion.form>
                      ) : (
                        <motion.div
                          key="book-flow"
                          ref={bookAppointmentRef}
                          id="contact-book-appointment"
                          className="scroll-mt-28 space-y-8 pb-2"
                          initial={{ opacity: 0, y: 14 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -12 }}
                          transition={{ duration: 0.28 }}
                        >
                          <div className="overflow-hidden rounded-[2rem] border border-black/[0.07] bg-[#f7f4ec] shadow-[0_20px_60px_-40px_rgba(0,0,0,0.35)]">
                            <div className="grid gap-8 p-6 sm:p-8 lg:grid-cols-[1fr_0.85fr] lg:items-center">
                              <div>
                                <p className="text-[11px] font-black uppercase tracking-[0.2em] text-[#c9a000]">
                                  Live scheduling
                                </p>
                                <p className="mt-2 text-2xl font-black leading-tight text-black sm:text-3xl">
                                  Pick a time that works for you
                                </p>
                                <p className="mt-4 text-sm leading-relaxed text-gray-600">
                                  Same Calendly integration as our homepage —
                                  open the calendar when you are ready.
                                </p>
                                <ul className="mt-6 space-y-3 text-sm font-semibold text-gray-800">
                                  {[
                                    "Talk with the InfoBoxx team about partnerships, products, or support",
                                    "Reschedule anytime from your confirmation email",
                                    "Prefer writing? Switch to “Send a message” above",
                                  ].map((line) => (
                                    <li key={line} className="flex gap-3">
                                      <CheckCircle2
                                        size={18}
                                        className="mt-0.5 shrink-0 text-[#c9a000]"
                                      />
                                      <span className="leading-snug">
                                        {line}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                                <motion.button
                                  type="button"
                                  onClick={openBooking}
                                  className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#FFD221] px-7 py-4 text-sm font-black text-black shadow-[0_16px_40px_-20px_rgba(255,210,33,0.75)] transition hover:bg-[#ffdf44] sm:w-auto"
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                >
                                  Book Appointment
                                  <ChevronDown
                                    size={18}
                                    className={`transition ${showCalendly ? "rotate-180" : ""}`}
                                    aria-hidden
                                  />
                                </motion.button>
                              </div>
                              <div className="relative flex min-h-[220px] items-center justify-center rounded-3xl border border-black/10 bg-white p-8">
                                <div
                                  className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,210,33,0.22),transparent_55%)]"
                                  aria-hidden
                                />
                                <div className="relative flex h-28 w-28 items-center justify-center rounded-3xl bg-gradient-to-br from-[#FFD221] to-[#ffdf44] shadow-lg ring-2 ring-black/5">
                                  <Calendar
                                    size={48}
                                    strokeWidth={2}
                                    className="text-black"
                                    aria-hidden
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div
                            ref={calendlyRef}
                            id="contact-calendly"
                            className="scroll-mt-28"
                          >
                            <AnimatePresence>
                              {showCalendly && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{
                                    duration: 0.35,
                                    ease: [0.22, 1, 0.36, 1],
                                  }}
                                  className="overflow-hidden"
                                >
                                  <div className="overflow-hidden rounded-[2rem] border border-black/10 bg-[#f7f4ec] shadow-[0_28px_80px_-48px_rgba(0,0,0,0.45)]">
                                    <div className="flex flex-wrap items-center justify-between gap-4 border-b border-black/10 bg-white px-6 py-4 sm:px-8">
                                      <div className="flex items-center gap-3">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#FFD221]">
                                          <Calendar
                                            size={20}
                                            strokeWidth={2.5}
                                            className="text-black"
                                            aria-hidden
                                          />
                                        </div>
                                        <div>
                                          <p className="text-xs font-black uppercase tracking-wider text-[#c9a000]">
                                            Live scheduling
                                          </p>
                                          <p className="text-sm font-black text-gray-900">
                                            Pick a consultation slot
                                          </p>
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => setShowCalendly(false)}
                                        className="text-xs font-black uppercase tracking-wide text-gray-500 underline-offset-2 hover:text-black hover:underline"
                                      >
                                        Hide calendar
                                      </button>
                                    </div>
                                    <div className="bg-[#fafaf8] p-2 sm:p-4">
                                      <InlineWidget
                                        url={CALENDLY_URL}
                                        styles={{
                                          height: "660px",
                                          minWidth: "280px",
                                        }}
                                        pageSettings={{
                                          hideEventTypeDetails: false,
                                          hideLandingPageDetails: false,
                                          backgroundColor: "fafaf8",
                                          textColor: "111827",
                                          primaryColor: "f5c518",
                                        }}
                                      />
                                    </div>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* sidebar */}
          <div className="space-y-5">
            {/* what happens next */}
            <motion.div
              {...fadeUp(0.12)}
              className="overflow-hidden rounded-[2rem] border border-black/[0.07] bg-white shadow-sm"
            >
              <div className="h-1 bg-gradient-to-r from-[#FFD221] to-transparent" />
              <div className="p-6">
                <p className="text-[11px] font-black uppercase tracking-[0.2em] text-[#c9a000]">
                  What happens next
                </p>
                <div className="mt-5 space-y-5">
                  {nextSteps.map(({ icon: Icon, title, text }, index) => (
                    <div key={title} className="flex gap-4">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#FFD221]/20 text-[#c9a000]">
                        <Icon size={17} />
                      </div>
                      <div>
                        <p className="text-sm font-black text-black">{title}</p>
                        <p className="mt-1 text-xs leading-relaxed text-gray-500">
                          {text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* call card */}
            <motion.a
              {...fadeUp(0.18)}
              href="tel:+233200000000"
              className="flex items-center justify-between rounded-[2rem] border border-[#FFD221]/40 bg-[#FFD221]/10 p-6 transition hover:bg-[#FFD221]/20"
            >
              <div className="flex items-center gap-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#FFD221] text-black">
                  <Phone size={19} />
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">
                    Prefer a call?
                  </p>
                  <p className="mt-0.5 text-lg font-black text-black">
                    +233 20 000 0000
                  </p>
                </div>
              </div>
              <ArrowRight size={20} className="text-black" />
            </motion.a>

            {/* hours card */}
            <motion.div
              {...fadeUp(0.24)}
              className="flex items-center gap-4 rounded-[2rem] border border-black/[0.07] bg-white p-6"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gray-950 text-[#FFD221]">
                <Clock size={19} />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">
                  Operating hours
                </p>
                <p className="mt-0.5 text-sm font-black text-black">
                  Mon–Fri: 8AM–8PM
                </p>
                <p className="text-xs text-gray-400">Sat: 9AM–4PM</p>
              </div>
            </motion.div>

            {/* map / location card */}
            <motion.div
              {...fadeUp(0.3)}
              className="overflow-hidden rounded-[2rem] border border-black/[0.07] bg-gray-950 text-white shadow-sm"
            >
              <div className="relative h-44 w-full overflow-hidden bg-gray-900 sm:h-48">
                <iframe
                  title="InfoBoxx office — 71 Swaniker Lane, Abelemkpe, Accra"
                  src="https://maps.google.com/maps?q=71+Swaniker+Lane,+Abelemkpe,+Accra,+Ghana&hl=en&z=16&output=embed"
                  className="absolute inset-0 h-full w-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-gray-950 to-transparent" />
                <div className="pointer-events-none absolute bottom-3 left-4 flex items-center gap-2 drop-shadow-sm">
                  <MapPin size={14} className="text-[#FFD221]" />
                  <p className="text-xs font-black text-white">
                    Abelemkpe, Accra
                  </p>
                </div>
              </div>
              <div className="p-5">
                <p className="text-xs font-black text-[#FFD221]">
                  Visit our office
                </p>
                <p className="mt-1 text-sm leading-snug text-gray-300">
                  1st Floor GWOSEVO Building, 71 Swaniker Lane, Abelemkpe,
                  Accra, Ghana
                </p>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=71+Swaniker+Lane+Abelemkpe+Accra+Ghana"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1 text-xs font-black text-[#FFD221] underline-offset-2 hover:underline"
                >
                  Open in Google Maps
                  <ArrowRight size={12} />
                </a>
              </div>
            </motion.div>

            {/* partner CTA */}
            <motion.div
              {...fadeUp(0.36)}
              className="rounded-[2rem] border border-[#FFD221]/30 bg-gray-950 p-6 text-white"
            >
              <p className="text-[11px] font-black uppercase tracking-[0.2em] text-[#FFD221]">
                Ready to partner?
              </p>
              <p className="mt-2 text-lg font-black leading-snug">
                Skip the form — start your onboarding directly.
              </p>
              <Link
                to="/partners#how-to-become-a-partner"
                className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-[#FFD221] px-6 py-3.5 text-sm font-black text-black transition hover:bg-[#ffdf44]"
              >
                Begin Partner Onboarding
                <ArrowRight size={15} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
